import type { EntityType, TicketEntity, DisplayProperty } from 'src/composables/ticket-entities/types';
import type {
  FetchOrganisationResponse,
  FetchUnitResponse,
  FetchSiteResponse,
  FetchOrganisationContactPersonResponse,
} from 'src/api/models';

export type { DisplayProperty };

// ============================================================================
// Panel theme — visual identity per entity slot
// ============================================================================

export interface EntityPanelTheme {
  badge: string;
  icon: string;
  bg: string;
}

export const ENTITY_THEMES: Record<string, EntityPanelTheme> = {
  unit: {
    badge: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-500/10',
  },
  organisation: {
    badge: 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400',
    icon: 'text-violet-500',
    bg: 'bg-violet-50 dark:bg-violet-500/10',
  },
  site: {
    badge: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: 'text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
  },
  orgContact: {
    badge: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400',
    icon: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-500/10',
  },
  siteContact: {
    badge: 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400',
    icon: 'text-orange-500',
    bg: 'bg-orange-50 dark:bg-orange-500/10',
  },
};

// ============================================================================
// Icon mapping per entity type
// ============================================================================

export const ENTITY_ICONS: Record<EntityType, string> = {
  unit: 'pi-truck',
  organisation: 'pi-building',
  site: 'pi-map-marker',
  contact_person: 'pi-user',
};

// ============================================================================
// Display name extraction
// ============================================================================

export function getEntityDisplayName(entity: TicketEntity, entityType: EntityType): string {
  switch (entityType) {
    case 'unit': {
      const u = entity as FetchUnitResponse;
      return u.name || (u.fleet_number != null ? `Fleet #${u.fleet_number}` : 'Unknown Unit');
    }
    case 'organisation': {
      const o = entity as FetchOrganisationResponse;
      return o.name || 'Unknown Organisation';
    }
    case 'site': {
      const s = entity as FetchSiteResponse;
      return s.name || s.street || 'Unknown Site';
    }
    case 'contact_person': {
      const c = entity as FetchOrganisationContactPersonResponse;
      return [c.first_name, c.midlet_name, c.last_name].filter(Boolean).join(' ') || 'Unknown Contact';
    }
  }
}

// ============================================================================
// Property extraction per entity type
// ============================================================================

export function getEntityDisplayProps(entity: TicketEntity, entityType: EntityType): DisplayProperty[] {
  switch (entityType) {
    case 'unit':
      return getUnitProps(entity as FetchUnitResponse);
    case 'organisation':
      return getOrganisationProps(entity as FetchOrganisationResponse);
    case 'site':
      return getSiteProps(entity as FetchSiteResponse);
    case 'contact_person':
      return getContactProps(entity as FetchOrganisationContactPersonResponse);
  }
}

function getUnitProps(u: FetchUnitResponse): DisplayProperty[] {
  return filterProps([
    u.fleet_number != null && { key: 'Fleet #', value: String(u.fleet_number), copyable: true },
    u.serial_number && { key: 'S/N', value: u.serial_number, copyable: true },
    u.category?.name && { key: 'Category', value: u.category.name },
    u.manufacture_name && { key: 'Make', value: u.manufacture_name },
    u.manufactured_year && { key: 'Year', value: String(u.manufactured_year) },
    u.status && { key: 'Status', value: u.status },
    u.organisation?.name && { key: 'Org', value: u.organisation.name },
  ]);
}

function getOrganisationProps(o: FetchOrganisationResponse): DisplayProperty[] {
  return filterProps([
    o.number != null && { key: 'Number', value: `#${o.number}`, copyable: true },
    o.type && { key: 'Type', value: o.type },
    o.address?.city && { key: 'City', value: o.address.city },
    o.vat_number && { key: 'VAT', value: o.vat_number, copyable: true },
  ]);
}

function getSiteProps(s: FetchSiteResponse): DisplayProperty[] {
  const address = [s.street, s.house_number, s.house_number_extension].filter(Boolean).join(' ');
  return filterProps([
    address && { key: 'Address', value: address, copyable: true },
    s.city && { key: 'City', value: [s.post_code, s.city].filter(Boolean).join(' ') },
    s.country && { key: 'Country', value: s.country },
  ]);
}

function getContactProps(c: FetchOrganisationContactPersonResponse): DisplayProperty[] {
  return filterProps([
    c.email && { key: 'Email', value: c.email, copyable: true },
    c.phone_number && { key: 'Phone', value: c.phone_number, copyable: true },
    c.mobile_phone_number && { key: 'Mobile', value: c.mobile_phone_number, copyable: true },
    c.organisation?.name && { key: 'Org', value: c.organisation.name },
  ]);
}

function filterProps(items: (DisplayProperty | false | undefined | null | '' | 0)[]): DisplayProperty[] {
  return items.filter((p): p is DisplayProperty => !!p);
}

// ============================================================================
// Search text extraction (for client-side filtering)
// ============================================================================

export function getEntitySearchText(entity: TicketEntity, entityType: EntityType): string {
  const name = getEntityDisplayName(entity, entityType);
  const props = getEntityDisplayProps(entity, entityType);
  return [name, ...props.map((p) => p.value)].join(' ').toLowerCase();
}
